import type { CreateUserInput, DeactivationPreview, OrgUser, Tag } from '@pm/schemas';
import { formatPhone } from '@pm/schemas';
import {
  Avatar,
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  EmptyState,
  ErrorState,
  Field,
  Input,
  LabelCaps,
  Menu,
  MenuContent,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
  PageHeader,
  SkeletonRows,
  Tabs,
  Tag as TagChip,
} from '@pm/ui';
import {
  MoreHorizontal,
  Pencil,
  Plus,
  RotateCcw,
  Search,
  ShieldCheck,
  Tags,
  Trash2,
  UserRoundX,
  Users,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { UserPicker } from '@/components/pickers';
import { api } from '@/lib/api';
import { useAuth, useMe } from '@/lib/auth';
import { fmtDateTime } from '@/lib/format';
import { keys, useInvalidating, useRoles, useTags, useUsers } from '@/lib/queries';
import { TAG_PALETTE } from '@/lib/tag-palette';
import { useToast } from '@/lib/toast';
import { CompanySection } from './CompanySection';
import { EditUserDialog } from './EditUserDialog';
import { CustomFieldsSection } from './CustomFieldsSection';
import { ParametersSection } from './ParametersSection';
import { IntegrationsSection } from './IntegrationsSection';
import { HolidaysSection } from './HolidaysSection';

export function AdminPage() {
  const { can } = useAuth();
  const [tab, setTab] = useState<
    'users' | 'company' | 'params' | 'tags' | 'holidays' | 'fields' | 'integrations'
  >(can('users.view') ? 'users' : 'tags');
  return (
    <div className="flex max-w-[1100px] flex-col gap-gap-lg">
      <PageHeader
        title="Administração"
        subtitle="Usuários, dados da empresa, parâmetros e cadastros de apoio da organização."
      />
      <Tabs
        value={tab}
        onChange={setTab}
        tabs={[
          ...(can('users.view') ? [{ value: 'users' as const, label: 'Usuários' }] : []),
          ...(can('settings.manage')
            ? [
                { value: 'company' as const, label: 'Dados da Empresa' },
                { value: 'params' as const, label: 'Parâmetros' },
              ]
            : []),
          { value: 'tags' as const, label: 'Tags' },
          { value: 'holidays' as const, label: 'Feriados' },
          { value: 'fields' as const, label: 'Campos personalizados' },
          ...(can('integrations.manage')
            ? [{ value: 'integrations' as const, label: 'Integrações' }]
            : []),
        ]}
      />
      {tab === 'users' ? (
        <UsersSection />
      ) : tab === 'company' ? (
        <CompanySection />
      ) : tab === 'params' ? (
        <ParametersSection />
      ) : tab === 'tags' ? (
        <TagsSection />
      ) : tab === 'holidays' ? (
        <HolidaysSection />
      ) : tab === 'fields' ? (
        <CustomFieldsSection />
      ) : (
        <IntegrationsSection />
      )}
    </div>
  );
}

/* ---------- Usuários ---------- */
function UsersSection() {
  const me = useMe();
  const toast = useToast();
  const { can } = useAuth();
  const [q, setQ] = useState('');
  const { data, isLoading, isError, error, refetch } = useUsers({ q: q || undefined });
  const { data: roles = [] } = useRoles();
  const [create, setCreate] = useState(false);
  const [rolesFor, setRolesFor] = useState<OrgUser | null>(null);
  const [editing, setEditing] = useState<OrgUser | null>(null);
  const [deactivate, setDeactivate] = useState<OrgUser | null>(null);
  const reactivate = useInvalidating(
    (id: string) => api.post(`/users/${id}/reactivate`),
    [['users']],
  );
  const users = data?.data ?? [];

  return (
    <div className="flex flex-col gap-gap">
      <div className="flex items-center gap-gap">
        <Input
          leading={<Search />}
          placeholder="Buscar por nome ou e-mail"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="w-[280px]"
        />
        {can('users.create') ? (
          <Button variant="primary" className="ml-auto" onClick={() => setCreate(true)}>
            <Plus /> Novo usuário
          </Button>
        ) : null}
      </div>
      <div className="overflow-x-auto rounded-md border border-border bg-surface-1">
        {isLoading ? (
          <SkeletonRows />
        ) : isError ? (
          <ErrorState message={error.message} onRetry={() => void refetch()} />
        ) : users.length === 0 ? (
          <EmptyState icon={Users} title="Nenhum usuário" />
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-border-strong bg-bg font-mono text-xs uppercase tracking-[var(--tracking-caps)] text-text-3">
                <th className="h-8 px-3 text-left font-medium">Usuário</th>
                <th className="h-8 px-3 text-left font-medium">Perfis</th>
                <th className="h-8 px-3 text-left font-medium">Status</th>
                <th className="h-8 px-3 text-left font-medium">Último acesso</th>
                <th className="h-8 w-10 px-3" />
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr
                  key={u.id}
                  className="h-row border-b border-border last:border-b-0 hover:bg-surface-2"
                >
                  <td className="px-3">
                    <span className="flex items-center gap-2">
                      <Avatar name={u.name} src={u.avatar_url} size="md" />
                      <span>
                        <span className="block font-medium">
                          {u.name}
                          {u.id === me.user.id ? (
                            <span className="ml-1.5 font-mono text-[10px] text-text-3">você</span>
                          ) : null}
                        </span>
                        <span className="block font-mono text-xs text-text-3">{u.email}</span>
                      </span>
                    </span>
                  </td>
                  <td className="px-3">
                    <span className="flex flex-wrap gap-1">
                      {u.roles.map((r) => (
                        <Badge
                          key={r.id}
                          mono={false}
                          tone={r.system_role === 'ADMIN' ? 'accent' : 'neutral'}
                        >
                          {r.name}
                        </Badge>
                      ))}
                      {u.roles.length === 0 ? (
                        <span className="text-sm text-text-3">Sem perfil</span>
                      ) : null}
                    </span>
                  </td>
                  <td className="px-3">
                    {u.membership_status === 'ACTIVE' && u.status === 'ACTIVE' ? (
                      <Badge tone="success">Ativo</Badge>
                    ) : (
                      <Badge tone="neutral">Inativo</Badge>
                    )}
                  </td>
                  <td className="px-3 font-mono text-xs text-text-3">
                    {fmtDateTime(u.last_login_at)}
                  </td>
                  <td className="px-3">
                    <Menu>
                      <MenuTrigger asChild>
                        <Button variant="ghost" size="icon-sm" aria-label="Ações">
                          <MoreHorizontal />
                        </Button>
                      </MenuTrigger>
                      <MenuContent align="end">
                        {can('users.edit') ? (
                          <MenuItem onSelect={() => setEditing(u)}>
                            <Pencil /> Editar
                          </MenuItem>
                        ) : null}
                        {can('users.manage_roles') ? (
                          <MenuItem onSelect={() => setRolesFor(u)}>
                            <ShieldCheck /> Perfis
                          </MenuItem>
                        ) : null}
                        {can('users.deactivate') ? (
                          <>
                            <MenuSeparator />
                            {u.membership_status === 'ACTIVE' ? (
                              <MenuItem
                                danger
                                disabled={u.id === me.user.id}
                                onSelect={() => setDeactivate(u)}
                              >
                                <UserRoundX /> Desativar
                              </MenuItem>
                            ) : (
                              <MenuItem
                                onSelect={() =>
                                  reactivate.mutate(u.id, {
                                    onSuccess: () => toast.success('Usuário reativado'),
                                    onError: (e) => toast.error(e),
                                  })
                                }
                              >
                                <RotateCcw /> Reativar
                              </MenuItem>
                            )}
                          </>
                        ) : null}
                      </MenuContent>
                    </Menu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <UserDialog open={create} onOpenChange={setCreate} roles={roles} />
      <EditUserDialog user={editing} onOpenChange={(o) => !o && setEditing(null)} />
      <RolesDialog user={rolesFor} onOpenChange={(o) => !o && setRolesFor(null)} roles={roles} />
      <DeactivateDialog user={deactivate} onOpenChange={(o) => !o && setDeactivate(null)} />
    </div>
  );
}

function UserDialog({
  open,
  onOpenChange,
  roles,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  roles: { id: string; name: string; system_role: string | null }[];
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title="Novo usuário" size="sm">
        <UserForm roles={roles} onOpenChange={onOpenChange} />
      </DialogContent>
    </Dialog>
  );
}

function UserForm({
  roles,
  onOpenChange,
}: {
  roles: { id: string; name: string; system_role: string | null }[];
  onOpenChange: (o: boolean) => void;
}) {
  const toast = useToast();
  const [f, setF] = useState(() => ({
    name: '',
    email: '',
    password: '',
    phone: '',
    role_ids: roles.filter((r) => r.system_role === 'COLLABORATOR').map((r) => r.id),
  }));
  const save = useInvalidating(
    (input: CreateUserInput) => api.post<OrgUser>('/users', input),
    [['users']],
  );
  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate(
          {
            name: f.name,
            email: f.email,
            password: f.password,
            phone: f.phone || undefined,
            role_ids: f.role_ids,
          },
          {
            onSuccess: () => (toast.success('Usuário criado', f.email), onOpenChange(false)),
            onError: (err) => toast.error(err),
          },
        );
      }}
    >
      <Field label="Nome" htmlFor="u-name">
        <Input
          id="u-name"
          autoFocus
          value={f.name}
          onChange={(e) => setF({ ...f, name: e.target.value })}
        />
      </Field>
      <Field label="E-mail" htmlFor="u-email">
        <Input
          id="u-email"
          type="email"
          mono
          value={f.email}
          onChange={(e) => setF({ ...f, email: e.target.value })}
        />
      </Field>
      <Field
        label="Senha inicial"
        htmlFor="u-pass"
        hint="Mínimo de 8 caracteres. Peça para trocar no primeiro acesso."
      >
        <Input
          id="u-pass"
          type="password"
          mono
          value={f.password}
          onChange={(e) => setF({ ...f, password: e.target.value })}
        />
      </Field>
      <Field label="Telefone" htmlFor="u-phone">
        <Input
          id="u-phone"
          mono
          type="tel"
          value={f.phone}
          onChange={(e) => setF({ ...f, phone: formatPhone(e.target.value) })}
          placeholder="(11) 98765-4321"
          maxLength={19}
        />
      </Field>
      <div className="flex flex-col gap-1.5">
        <LabelCaps>Perfis</LabelCaps>
        {roles.map((r) => (
          <label key={r.id} className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={f.role_ids.includes(r.id)}
              onCheckedChange={(v) =>
                setF({
                  ...f,
                  role_ids: v ? [...f.role_ids, r.id] : f.role_ids.filter((x) => x !== r.id),
                })
              }
            />
            {r.name}
          </label>
        ))}
      </div>
      <DialogFooter>
        <Button variant="ghost" type="button" onClick={() => onOpenChange(false)}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          type="submit"
          loading={save.isPending}
          disabled={f.name.length < 2 || !f.email || f.password.length < 8}
        >
          Criar
        </Button>
      </DialogFooter>
    </form>
  );
}

function RolesDialog({
  user,
  onOpenChange,
  roles,
}: {
  user: OrgUser | null;
  onOpenChange: (o: boolean) => void;
  roles: { id: string; name: string; system_role: string | null; permissions: string[] }[];
}) {
  return (
    <Dialog open={user !== null} onOpenChange={onOpenChange}>
      <DialogContent
        title={`Perfis de ${user?.name ?? ''}`}
        description="Perfis são presets de permissões. Mudanças valem no próximo acesso."
        size="sm"
      >
        {user ? <RolesForm user={user} roles={roles} onOpenChange={onOpenChange} /> : null}
      </DialogContent>
    </Dialog>
  );
}

/** SPEC §41 — desativação mostra pendências e pede sucessor. */
function RolesForm({
  user,
  roles,
  onOpenChange,
}: {
  user: OrgUser;
  roles: { id: string; name: string; system_role: string | null; permissions: string[] }[];
  onOpenChange: (o: boolean) => void;
}) {
  const toast = useToast();
  const [ids, setIds] = useState<string[]>(() => user.roles.map((r) => r.id));
  const save = useInvalidating(
    (role_ids: string[]) => api.put(`/users/${user.id}/roles`, { role_ids }),
    [['users']],
  );
  return (
    <>
      <ul className="flex flex-col gap-2">
        {roles.map((r) => (
          <li key={r.id}>
            <label className="flex items-start gap-2 text-sm">
              <Checkbox
                className="mt-0.5"
                checked={ids.includes(r.id)}
                onCheckedChange={(v) => setIds(v ? [...ids, r.id] : ids.filter((x) => x !== r.id))}
              />
              <span>
                <span className="block font-medium">{r.name}</span>
                <span className="block font-mono text-[11px] text-text-3">
                  {r.permissions.length} permissões
                </span>
              </span>
            </label>
          </li>
        ))}
      </ul>
      <DialogFooter>
        <Button variant="ghost" onClick={() => onOpenChange(false)}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          loading={save.isPending}
          onClick={() =>
            save.mutate(ids, {
              onSuccess: () => (toast.success('Perfis atualizados'), onOpenChange(false)),
              onError: (e) => toast.error(e),
            })
          }
        >
          Salvar
        </Button>
      </DialogFooter>
    </>
  );
}

function DeactivateDialog({
  user,
  onOpenChange,
}: {
  user: OrgUser | null;
  onOpenChange: (o: boolean) => void;
}) {
  return (
    <Dialog open={user !== null} onOpenChange={onOpenChange}>
      <DialogContent
        title={`Desativar ${user?.name ?? ''}`}
        description="A pessoa perde o acesso. O histórico é preservado."
        size="sm"
      >
        {user ? <DeactivateForm user={user} onOpenChange={onOpenChange} /> : null}
      </DialogContent>
    </Dialog>
  );
}

function DeactivateForm({
  user,
  onOpenChange,
}: {
  user: OrgUser;
  onOpenChange: (o: boolean) => void;
}) {
  const toast = useToast();
  const [preview, setPreview] = useState<DeactivationPreview | null>(null);
  const [successor, setSuccessor] = useState<string | null>(null);
  useEffect(() => {
    void api
      .get<DeactivationPreview>(`/users/${user.id}/deactivation-preview`)
      .then(setPreview)
      .catch((e) => toast.error(e));
  }, [user.id, toast]);
  const run = useInvalidating(
    (successor_user_id: string | null) =>
      api.post(`/users/${user.id}/deactivate`, successor_user_id ? { successor_user_id } : {}),
    [['users'], ['tasks']],
  );
  const needs = (preview?.pending_tasks ?? 0) > 0;
  return (
    <>
      {preview ? (
        <dl className="grid grid-cols-[1fr_auto] gap-y-1.5 rounded border border-border bg-surface-2 px-3 py-2 text-sm">
          <dt className="text-text-2">Tarefas pendentes como responsável</dt>
          <dd className="font-mono">{preview.pending_tasks}</dd>
          <dt className="text-text-2">Times</dt>
          <dd className="font-mono">{preview.teams}</dd>
          <dt className="text-text-2">Aprovações pendentes</dt>
          <dd className="font-mono">{preview.pending_approvals}</dd>
        </dl>
      ) : (
        <p className="text-sm text-text-3">Calculando pendências…</p>
      )}
      {needs ? (
        <div className="mt-4 flex flex-col gap-1.5">
          <LabelCaps>Transferir pendências para</LabelCaps>
          <UserPicker
            value={successor}
            onChange={setSuccessor}
            nullable={false}
            placeholder="Escolha o sucessor"
          />
        </div>
      ) : null}
      <DialogFooter>
        <Button variant="ghost" onClick={() => onOpenChange(false)}>
          Cancelar
        </Button>
        <Button
          variant="danger"
          loading={run.isPending}
          disabled={!preview || (needs && !successor) || successor === user.id}
          onClick={() =>
            run.mutate(successor, {
              onSuccess: () => (toast.success('Usuário desativado'), onOpenChange(false)),
              onError: (e) => toast.error(e),
            })
          }
        >
          Desativar
        </Button>
      </DialogFooter>
    </>
  );
}

/* ---------- Tags ---------- */
const PALETTE: readonly string[] = TAG_PALETTE;

function TagsSection() {
  const toast = useToast();
  const { can } = useAuth();
  const { data: tags = [], isLoading } = useTags();
  const [name, setName] = useState('');
  const [color, setColor] = useState(PALETTE[0]!);
  const [remove, setRemove] = useState<Tag | null>(null);
  const create = useInvalidating(
    (input: { name: string; color: string }) => api.post<Tag>('/tags', input),
    [keys.tags],
  );
  const del = useInvalidating(
    (id: string) => api.delete(`/tags/${id}`),
    [keys.tags, ['tasks'], ['task']],
  );
  const manage = can('tasks.manage_tags');

  return (
    <div className="grid gap-gap-lg lg:grid-cols-[1fr_320px]">
      <div className="rounded-md border border-border bg-surface-1">
        {isLoading ? (
          <SkeletonRows rows={3} />
        ) : tags.length === 0 ? (
          <EmptyState
            icon={Tags}
            title="Nenhuma tag"
            description="Tags classificam demandas e aparecem nos cards e na lista."
          />
        ) : (
          <ul>
            {tags.map((t) => (
              <li
                key={t.id}
                className="flex h-row items-center gap-3 border-b border-border px-4 last:border-b-0"
              >
                <TagChip color={t.color ?? undefined}>{t.name}</TagChip>
                <span className="font-mono text-xs text-text-3">{t.color ?? 'sem cor'}</span>
                {t.type === 'SYSTEM' ? <Badge tone="neutral">sistema</Badge> : null}
                {manage && t.type === 'USER' ? (
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="ml-auto"
                    aria-label="Excluir"
                    onClick={() => setRemove(t)}
                  >
                    <Trash2 />
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
      {manage ? (
        <form
          className="flex flex-col gap-3 self-start rounded-md border border-border bg-surface-1 p-4"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate(
              { name: name.trim(), color },
              {
                onSuccess: () => (setName(''), toast.success('Tag criada')),
                onError: (err) => toast.error(err),
              },
            );
          }}
        >
          <LabelCaps>Nova tag</LabelCaps>
          <Input placeholder="Nome" value={name} onChange={(e) => setName(e.target.value)} />
          <div className="flex flex-wrap gap-2">
            {PALETTE.map((c) => (
              <button
                key={c}
                type="button"
                aria-label={c}
                onClick={() => setColor(c)}
                style={{ background: c }}
                className={`size-5 rounded-full border-2 ${color === c ? 'border-text-1 shadow-glow' : 'border-transparent'}`}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-text-2">Prévia:</span>
            <TagChip color={color}>{name || 'tag'}</TagChip>
          </div>
          <Button
            type="submit"
            variant="primary"
            size="sm"
            loading={create.isPending}
            disabled={!name.trim()}
          >
            <Plus /> Criar tag
          </Button>
        </form>
      ) : null}
      <ConfirmDialog
        open={remove !== null}
        onOpenChange={() => setRemove(null)}
        title={`Excluir tag ${remove?.name ?? ''}`}
        description="A tag some de todas as demandas."
        confirmLabel="Excluir"
        danger
        loading={del.isPending}
        onConfirm={() =>
          remove &&
          del.mutate(remove.id, {
            onSuccess: () => (toast.success('Tag excluída'), setRemove(null)),
            onError: (e) => toast.error(e),
          })
        }
      />
    </div>
  );
}
